'use client';

import { Loader2, CheckCircle, AlertCircle, Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Tool UI for Export Moment tool
 * Displays export status with download link when ready
 */
export function ExportMomentToolUI({ part, status }: any) {
  const result = part.result;

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-sm text-blue-900 dark:text-blue-100">
          Starting export...
        </span>
      </div>
    );
  }

  if (!result.success) {
    const isCreditsError = result.error?.toLowerCase().includes('credit');
    const upgradeUrl = result.upgradeUrl;

    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 overflow-hidden">
        <div className="flex items-start gap-2 p-3">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-100">
              {isCreditsError ? 'Insufficient credits' : 'Export failed'}
            </p>
            {result.error && (
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                {result.error}
              </p>
            )}
          </div>
        </div>
        {isCreditsError && upgradeUrl && (
          <div className="px-3 pb-3">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => window.open(upgradeUrl, '_blank')}
            >
              Upgrade Plan
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  const estimatedTime = result.estimatedTime || 120;
  const downloadUrl = result.downloadUrl;

  return (
    <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
      <div className="flex items-center gap-2 p-3">
        {downloadUrl ? (
          <>
            <CheckCircle className="w-4 h-4 text-purple-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
                Export complete!
              </p>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                Your video is ready to download
              </p>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
                Export started
              </p>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                Processing will take ~{Math.ceil(estimatedTime / 60)} minute{estimatedTime > 60 ? 's' : ''}
              </p>
            </div>
          </>
        )}
      </div>

      {downloadUrl && (
        <div className="px-3 pb-3">
          <Button
            size="sm"
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => window.open(downloadUrl, '_blank')}
          >
            <Download className="w-3 h-3 mr-1" />
            Download Video
          </Button>
        </div>
      )}

      {!downloadUrl && (
        <div className="px-3 pb-3">
          <p className="text-xs text-purple-600 dark:text-purple-400">
            💡 You'll receive an email when your export is ready
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Tool UI for Export Memo tool
 * Similar to ExportMomentToolUI but for memos
 */
export function ExportMemoToolUI({ part, status }: any) {
  const result = part.result;

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-sm text-blue-900 dark:text-blue-100">
          Starting memo export...
        </span>
      </div>
    );
  }

  if (!result.success) {
    const isCreditsError = result.error?.toLowerCase().includes('credit');
    const upgradeUrl = result.upgradeUrl;

    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 overflow-hidden">
        <div className="flex items-start gap-2 p-3">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-100">
              {isCreditsError ? 'Insufficient credits' : 'Export failed'}
            </p>
            {result.error && (
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                {result.error}
              </p>
            )}
          </div>
        </div>
        {isCreditsError && upgradeUrl && (
          <div className="px-3 pb-3">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => window.open(upgradeUrl, '_blank')}
            >
              Upgrade Plan
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  const estimatedTime = result.estimatedTime || 180;
  const downloadUrl = result.downloadUrl;

  return (
    <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
      <div className="flex items-center gap-2 p-3">
        {downloadUrl ? (
          <>
            <CheckCircle className="w-4 h-4 text-purple-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
                Memo export complete!
              </p>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                Your edited video is ready
              </p>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
                Rendering memo with edits
              </p>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                Processing will take ~{Math.ceil(estimatedTime / 60)} minute{estimatedTime > 60 ? 's' : ''}
              </p>
            </div>
          </>
        )}
      </div>

      {downloadUrl && (
        <div className="px-3 pb-3">
          <Button
            size="sm"
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => window.open(downloadUrl, '_blank')}
          >
            <Download className="w-3 h-3 mr-1" />
            Download Edited Video
          </Button>
        </div>
      )}

      {!downloadUrl && (
        <div className="px-3 pb-3">
          <p className="text-xs text-purple-600 dark:text-purple-400">
            💡 You'll receive an email when your export is ready
          </p>
        </div>
      )}
    </div>
  );
}
